import type { FC, ReactNode } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import SimpleLoader from "~/components/SimpleLoader"
import useProposalSavings from "~/containers/Proposal/state/savings/use-proposal-savings"
import {
  formatDecimal,
  formatInteger,
  parseEscalatorRate,
} from "~/utils/finance/format"

import { DEVICE } from "../../../../constants/device-widths"
import { brochureState } from "../../state/brochure/brochure-state"
import { isTpoProductSelectedState } from "../../state/loan-products/tpo-loan-product"
import { useProposalContext } from "../../state/proposal/proposal-context"
import { proposalLocalState } from "../../state/proposal/proposal-state"
import { utilitySavingsPendingState } from "../../state/sync-state"
import { calculatedUtilityRateState } from "../../state/utility-costs-and-savings/utility-costs-and-savings-state"

const Wrapper = styled.main`
  display: flex;
  width: 100%;
  margin-top: clamp(20px, 8vw, 110px);
  gap: var(--space-s);
  justify-content: center;
  @media ${DEVICE.tablet} {
    gap: var(--space-xl);
    align-items: flex-end;
  }
`
const StyledRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 40%;
  height: 100%;
  justify-content: space-between;
  gap: var(--space-s);
  @media ${DEVICE.tablet} {
    flex-direction: row;
    align-items: flex-end;
    height: 100%;
    width: 100%;
  }
`
const StyledBox = styled.div<{ side: string }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  p {
    font-size: var(--step--1);
  }
  div {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 60px;
    @media ${DEVICE.tablet} {
      height: clamp(50px, 7vmax, 100px);
    }
    background: linear-gradient(180deg, #e7f3fa 0%, rgba(247, 247, 247, 0) 100%);
    ${({ side }) => side === `left` && `background: #F7F7F7;`}
    color: ${({ side }) =>
      side === `left` ? `var(--blue-dark-font)` : `var(--blue)`};
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.14);
    border-radius: 10px;
    h3 {
      font-size: clamp(16px, 2vw, 24px);
      font-weight: 600;
    }
  }
`

type BoxProps = {
  label: string
  stat: ReactNode
}

const Box: FC<BoxProps & { side: string }> = ({ label, stat, side }) => {
  return (
    <StyledBox side={side}>
      <p>{label}</p>
      <div>
        <h3>{stat}</h3>
      </div>
    </StyledBox>
  )
}

const Row: FC<{ boxes: BoxProps[]; side: string }> = ({ boxes, side }) => {
  return (
    <StyledRow>
      {boxes.map((box) => (
        <Box key={box.label} label={box.label} stat={box.stat} side={side} />
      ))}
    </StyledRow>
  )
}

export const Stats: FC = () => {
  const { t } = useTranslation()
  const { annualSolarProduction } = useRecoilValue(brochureState)
  const utilityRate = useRecoilValue(calculatedUtilityRateState)
  const { utilityRateEscalationPercentage, annualEnergyUse } =
    useRecoilValue(proposalLocalState)
  const utilitySavingsPending = useRecoilValue(utilitySavingsPendingState)

  const isTpoProductSelected = useRecoilValue(isTpoProductSelectedState)
  const proposal = useProposalContext()
  const { data: savings, isError: isSavingsError } = useProposalSavings(
    proposal.local.id || ``,
  )
  const solarRate = savings?.solarRate ?? 0
  const isSavingsPending = savings === undefined && !isSavingsError

  const annualSolarIncrease = (() => {
    if (proposal.local.financeType === `lightreach`) {
      return (proposal.remote.selectedLoanProduct?.escalator ?? 0) * 100
    }

    return (
      proposal.remote.selectedLoanProduct?.escalator ??
      parseEscalatorRate(
        proposal.remote.selectedLoanProduct?.providerMeta?.escalatorRate,
      ) ??
      0
    )
  })()

  if (utilitySavingsPending) {
    return (
      <Wrapper>
        <Row boxes={[]} side="left" />
        <Row boxes={[]} side="right" />
      </Wrapper>
    )
  }

  const solarRateBox = {
    label: isTpoProductSelected ? t(`yearOneSolarRate`) : t(`averageSolarRate`),
    stat: isSavingsPending ? (
      <SimpleLoader />
    ) : (
      `$${formatDecimal(solarRate, 3)}/kWh`
    ),
  }
  const annualSolarIncreaseBox = {
    label: t(`annualSolarIncrease`),
    stat: formatDecimal(annualSolarIncrease) + `%`,
  }

  return (
    <Wrapper>
      <Row
        boxes={[
          {
            label: t(`effectiveUtilityRate`),
            stat: `$${formatDecimal(utilityRate)}/kWh`,
          },
          {
            label: t(`annualUtilityIncrease`),
            stat: `${formatDecimal(utilityRateEscalationPercentage)}%`,
          },
          {
            label: t(`annualEnergyConsumption`),
            stat: `${formatInteger(annualEnergyUse ?? 0)} kWh`,
          },
        ]}
        side="left"
      />
      <Row
        boxes={[
          solarRateBox,
          annualSolarIncreaseBox,
          {
            label: t(`annualSolarProduction`),
            stat: `${formatInteger(annualSolarProduction ?? 0)} kWh`,
          },
        ]}
        side="right"
      />
    </Wrapper>
  )
}
